import { z } from 'zod';
import type { SingularityLevier } from './singularity-setup.js';

/**
 * The pivot and the recipient of a matrix (issue #16, `docs/specs/matrices.md`).
 *
 * Both are chosen in the wizard, stored on the matrix, frozen at the first
 * generation, and read by every generation and regeneration: the pivot through
 * the `{{pivot}}` prompt variable, the recipient (issue #25) through the
 * `Destinataire :` line and the `destinataire` prompt sections.
 */

/**
 * The setup field each palier's activation bullets decline. The order is the
 * grid's « Pivot » sort order.
 */
export const MATRIX_PIVOTS = [
  'driver',
  'levier_implication',
  'principe_action',
  'levier_valeur',
] as const;
export const matrixPivotSchema = z.enum(MATRIX_PIVOTS);
export type MatrixPivot = z.infer<typeof matrixPivotSchema>;

/** Every existing matrix took this value, which keeps its former behaviour. */
export const DEFAULT_MATRIX_PIVOT: MatrixPivot = 'levier_valeur';

/** What the UI shows, and what `{{pivot}}` receives. */
export const MATRIX_PIVOT_LABELS: Readonly<Record<MatrixPivot, string>> = {
  driver: 'Driver',
  levier_implication: "Levier d'implication",
  principe_action: "Principe d'action",
  levier_valeur: 'Levier de valeur',
};

/** The section title of the wizard's setup list. */
export const MATRIX_PIVOT_PLURAL_LABELS: Readonly<Record<MatrixPivot, string>> = {
  driver: 'Drivers',
  levier_implication: "Leviers d'implication",
  principe_action: "Principes d'action",
  levier_valeur: 'Leviers de valeur',
};

/** The tooltip of each option of the pivot selector. */
export const MATRIX_PIVOT_MEANINGS: Readonly<Record<MatrixPivot, string>> = {
  driver: "l'esprit et la voix de l'étape",
  levier_implication: "le pourquoi de l'étape",
  principe_action: "le comment de l'étape",
  levier_valeur: "le quoi de l'étape",
};

/** The singularity setup field a pivot reads, on each of the 6 steps. */
export const MATRIX_PIVOT_SETUP_FIELD = {
  driver: 'driver',
  levier_implication: 'levierImplication',
  principe_action: 'principeAction',
  levier_valeur: 'levierValeur',
} as const satisfies Readonly<Record<MatrixPivot, keyof SingularityLevier>>;

/** The setup field names a pivot can read. */
export type MatrixPivotSetupField = (typeof MATRIX_PIVOT_SETUP_FIELD)[MatrixPivot];

/**
 * How many of the setup's steps carry a non-blank value for `pivot`. A pivot
 * is selectable, and a generation may start, only at 6 out of 6.
 */
export function countPivotFilledSteps(
  setup: readonly Pick<SingularityLevier, MatrixPivotSetupField>[],
  pivot: MatrixPivot,
): number {
  const field = MATRIX_PIVOT_SETUP_FIELD[pivot];
  return setup.filter((step) => (step[field]?.trim() ?? '') !== '').length;
}

/**
 * Who receives the action the matrix subject describes (issues #21, #25).
 * Four fixed values and `autre`, whose free text lives in
 * `Matrix.destinataireAutre`. « Non défini » is not a value: it is `null`.
 * The order is the order of the wizard's radio group and of the grid's
 * filter, « Non défini » coming first in both.
 */
export const MATRIX_DESTINATAIRES = [
  'clients',
  'salaries',
  'collegues',
  'manager',
  'autre',
] as const;
export const matrixDestinataireSchema = z.enum(MATRIX_DESTINATAIRES);
export type MatrixDestinataire = z.infer<typeof matrixDestinataireSchema>;

/** A new matrix has no recipient. */
export const DEFAULT_MATRIX_DESTINATAIRE: MatrixDestinataire | null = null;

/** Longest free text « Autre » accepts, counted after trimming. */
export const MATRIX_DESTINATAIRE_AUTRE_MAX_LENGTH = 120;

export const MATRIX_DESTINATAIRE_LABELS: Readonly<Record<MatrixDestinataire, string>> = {
  clients: 'Clients',
  salaries: "Salariés de l'entreprise",
  collegues: 'Mes collègues',
  manager: 'Mon manager',
  autre: 'Autre',
};

/** What the UI shows when there is no recipient. Never sent to the model. */
export const MATRIX_DESTINATAIRE_NONE_LABEL = 'Non défini';

/** The two fields that together make « le destinataire » of a matrix. */
export interface MatrixDestinataireFields {
  destinataire: MatrixDestinataire | null;
  destinataireAutre: string | null;
}

/**
 * The single place that turns a recipient into what the model receives (the
 * `Destinataire :` line, the récapitulatif's help line): the label of a fixed
 * value, or for `autre` the free text, trimmed. Null when there is no
 * recipient, an empty `autre` included: a null result is what « destinataire
 * défini » means everywhere (prompt sections, composed context).
 */
export function resolveDestinataireValue(matrix: MatrixDestinataireFields): string | null {
  if (matrix.destinataire === null) {
    return null;
  }
  if (matrix.destinataire === 'autre') {
    const text = matrix.destinataireAutre?.trim() ?? '';
    return text === '' ? null : text;
  }
  return MATRIX_DESTINATAIRE_LABELS[matrix.destinataire];
}

/**
 * The single place that turns a recipient into what the interface shows (card,
 * list, Cadrage): the same text, « Non défini » in place of null.
 */
export function resolveDestinataireLabel(matrix: MatrixDestinataireFields): string {
  return resolveDestinataireValue(matrix) ?? MATRIX_DESTINATAIRE_NONE_LABEL;
}
